import SEO from '../components/SEO';
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, User, ArrowRight, Tag, Loader2 } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import { blogAPI } from '../services/api';

const staticArticles = [
  {
    id: 'static-vider-maison-parents',
    title: 'Vider la maison de ses parents : conseils pratiques et émotionnels',
    slug: 'vider-maison-parents-conseils',
    excerpt: 'Méthode des 4 boîtes, gestion de la fratrie, souvenirs à préserver et moment où déléguer : les conseils terrain d\'une équipe du bassin annécien.',
    image: '/blog/vider-maison-parents-conseils/hero.jpg',
    date: '2026-08-08',
    author: 'DEBECO',
    category: 'Conseils'
  },
  {
    id: 'static-succession-guide',
    title: 'Débarras succession à Annecy : le guide complet pour les héritiers',
    slug: 'debarras-succession-annecy-guide',
    excerpt: 'Délais légaux, rôle du notaire, tri émotionnel, devenir des objets : tout ce que les héritiers doivent savoir avant de vider un logement à Annecy.',
    image: '/blog/debarras-succession-annecy-guide/hero.jpg',
    date: '2026-04-11',
    author: 'DEBECO',
    category: 'Succession'
  },
  {
    id: 'static-prix-debarras',
    title: 'Combien coûte un débarras de maison à Annecy en 2026 ?',
    slug: 'prix-debarras-maison-annecy',
    excerpt: 'Tarifs réels, fourchettes de prix par type de bien, facteurs qui influencent le coût et cas du débarras gratuit. Guide complet pour Annecy et le bassin annécien.',
    image: '/blog/prix-debarras-maison-annecy/hero.jpg',
    date: '2026-04-10',
    author: 'DEBECO',
    category: 'Tarifs'
  }
];

const Blog = () => {
  const [blogArticles, setBlogArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(null);
  
  const allArticles = [...staticArticles, ...blogArticles];
  const filteredArticles = selectedCategory
    ? allArticles.filter(a => a.category === selectedCategory)
    : allArticles;
  const categories = [...new Set(allArticles.map(article => article.category))];

  useEffect(() => {
    loadArticles();
  }, []);

  const loadArticles = async () => {
    try {
      setLoading(true);
      const articles = await blogAPI.getAll(0, 100);
      setBlogArticles(articles);
    } catch (error) {
      console.error('Error loading articles:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-blue-900 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <SEO 
        title="Blog - Conseils débarras et recyclage | DEBECO"
        description="Guides, conseils et actualités sur le débarras, le vide-maison et le recyclage responsable en Savoie et Haute-Savoie."
        canonical="/blog"
      />
      {/* Hero */}
      <section className="bg-gradient-to-br from-blue-950 via-blue-900 to-blue-950 text-white py-20">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Blog & Conseils</h1>
          <p className="text-xl text-blue-100 max-w-3xl mx-auto">
            Guides, conseils et actualités sur le débarras et le recyclage
          </p>
        </div>
      </section>

      {/* Categories */}
      <section className="py-8 bg-white border-b">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap gap-3 justify-center">
            <span 
              onClick={() => setSelectedCategory(null)}
              className={`px-4 py-2 rounded-full text-sm font-medium cursor-pointer transition-colors ${
                !selectedCategory 
                  ? 'bg-blue-900 text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-emerald-100 hover:text-emerald-700'
              }`}
            >
              Tous les articles
            </span>
            {categories.map((category) => (
              <span 
                key={category} 
                onClick={() => setSelectedCategory(category)}
                className={`px-4 py-2 rounded-full text-sm font-medium cursor-pointer transition-colors ${
                  selectedCategory === category
                    ? 'bg-blue-900 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-emerald-100 hover:text-emerald-700'
                }`}
              >
                {category}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Articles Grid */}
      <section className="py-16 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredArticles.map((article) => (
              <Card key={article.id} className="overflow-hidden hover:shadow-2xl transition-all border-2 hover:border-emerald-500 group">
                <Link to={`/blog/${article.slug}`} className="block h-48 overflow-hidden">
                  <img 
                    src={article.image} 
                    alt={article.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    loading="lazy"
                    width="400"
                    height="192"
                  />
                </Link>
                
                <CardContent className="p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="bg-emerald-100 text-emerald-700 px-3 py-1 rounded-full text-xs font-medium">
                      {article.category}
                    </span>
                  </div>
                  
                  <h3 className="font-bold text-xl text-blue-900 mb-3 line-clamp-2 group-hover:text-emerald-700 transition-colors">
                    {article.title}
                  </h3>
                  
                  <p className="text-gray-600 mb-4 line-clamp-3 text-sm">
                    {article.excerpt}
                  </p>
                  
                  <div className="flex items-center justify-between text-sm text-gray-500 mb-4 pb-4 border-b border-gray-200">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      <span>{new Date(article.date).toLocaleDateString('fr-FR')}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4" />
                      <span>{article.author}</span>
                    </div>
                  </div>
                  
                  <Link 
                    to={`/blog/${article.slug}`}
                    className="inline-flex items-center text-emerald-600 hover:text-emerald-700 font-medium group/link"
                  >
                    Lire l'article
                    <ArrowRight className="ml-2 w-4 h-4 group-hover/link:translate-x-1 transition-transform" />
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <Card className="max-w-3xl mx-auto border-2 border-blue-900">
            <CardContent className="p-8 text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-900 to-blue-700 rounded-full flex items-center justify-center mx-auto mb-4">
                <Tag className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-blue-900 mb-3">Restez informés</h2>
              <p className="text-gray-600 mb-6">
                Recevez nos derniers articles et conseils directement dans votre boîte mail
              </p>
              <div className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
                <input 
                  type="email" 
                  placeholder="Votre adresse email"
                  className="flex-1 px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-emerald-500 transition-colors"
                />
                <button className="bg-gradient-to-r from-emerald-600 to-emerald-500 hover:from-emerald-700 hover:to-emerald-600 text-white px-6 py-3 rounded-lg font-medium transition-all shadow-lg">
                  S'abonner
                </button>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-blue-900 to-emerald-800 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Besoin d'un débarras ?</h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Contactez-nous pour un devis gratuit et personnalisé
          </p>
          <Link to="/contact">
            <button className="bg-white text-blue-900 hover:bg-gray-100 px-8 py-4 rounded-lg font-semibold text-lg shadow-xl transition-all">
              Demander un devis
              <ArrowRight className="inline ml-2 w-5 h-5" />
            </button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Blog;